import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { api, useAuth } from "@/hooks/useAuth";
import "./DivineLanding.css";

/**
 * KALKI · DivineLanding — Continuous Scroll
 *
 * Two-layer cinematic parallax over a static violet dotted-grid background.
 *   Layer 1 (Divine Avatar) — moves slowly (30–70px) via Framer Motion useScroll.
 *   Layer 2 (Content)       — moves natively with the page scroll.
 *   Background              — static dotted grid on <body>.
 *
 * Structure (natural vertical scroll, NO stepper, NO scroll hijacking):
 *   A. Hero                               (10-kalki)
 *   B. What is KALKI                      (08-krishna)
 *   C. Intention + Wisdom (pre-login)     (ambient copy)
 *   D. Section header "Ten Avatars"
 *   E. Ten alternating feature sections   (01-matsya → 10-kalki)
 *   F. Chat/Talk showcase
 *   G. Methodology (Vedic × AI)
 *   H. Final Darshan + CTA
 */

const DASH = {
  matsya:      "/assets/divine/dashavatara/01-matsya.png",
  kurma:       "/assets/divine/dashavatara/02-kurma.png",
  varaha:      "/assets/divine/dashavatara/03-varaha.png",
  narasimha:   "/assets/divine/dashavatara/04-narasimha.png",
  vamana:      "/assets/divine/dashavatara/05-vamana.png",
  parashurama: "/assets/divine/dashavatara/06-parashurama.png",
  rama:        "/assets/divine/dashavatara/07-rama.png",
  krishna:     "/assets/divine/dashavatara/08-krishna.png",
  buddha:      "/assets/divine/dashavatara/09-buddha.png",
  kalki:       "/assets/divine/dashavatara/10-kalki.png",
};

const DASHAVATARA = [
  { id: "matsya", num: "01", name: "Matsya", devanagari: "मत्स्य",
    theme: "The preservation of knowledge",
    feature: "Your Vedic Chart",
    body: "Explore your birth details, Lagna, nakshatra, planetary positions, divisional charts and dashas — the quiet architecture of a life.",
    cta: "Discover My Chart" },
  { id: "kurma", num: "02", name: "Kurma", devanagari: "कूर्म",
    theme: "The strength of a steady foundation",
    feature: "Know Your Character",
    body: "Understand your inner strengths, life themes, nakshatras, Shadbala and Ashtakavarga — the terrain beneath every choice.",
    cta: "Understand Myself" },
  { id: "varaha", num: "03", name: "Varaha", devanagari: "वराह",
    theme: "The recovery of what lies beneath",
    feature: "My Life Journey",
    body: "Walk past → present → future with optional life-event reflection. Context, not prophecy.",
    cta: "Explore My Journey" },
  { id: "narasimha", num: "04", name: "Narasimha", devanagari: "नरसिंह",
    theme: "The courage of protection",
    feature: "Guidance With Care",
    body: "Privacy, wellbeing boundaries and responsible guidance — never fear-based, never prescriptive.",
    cta: "Explore KALKI's Approach" },
  { id: "vamana", num: "05", name: "Vamana", devanagari: "वामन",
    theme: "The power of meaningful steps",
    feature: "My Karma",
    body: "Three small, practical actions that turn insight into living. Small steps shift large patterns.",
    cta: "Begin My Karma" },
  { id: "parashurama", num: "06", name: "Parashurama", devanagari: "परशुराम",
    theme: "The clarity to distinguish what matters",
    feature: "Understand Why",
    body: "Every insight is explained through computed Vedic timing and classical methodology. Clarity, not mystery.",
    cta: "See the Reasoning" },
  { id: "rama", num: "07", name: "Rama", devanagari: "राम",
    theme: "The value of meaningful relationships",
    feature: "Love & Compatibility",
    body: "Reflection and compatibility traditions — with partner consent and gentle honesty.",
    cta: "Explore Relationships" },
  { id: "krishna", num: "08", name: "Krishna", devanagari: "कृष्ण",
    theme: "Wisdom through conversation",
    feature: "Chat & Talk With KALKI",
    body: "Ask about life, choices, hopes and dreams — any time. Voice or text, KALKI listens before it answers.",
    cta: "Talk to KALKI" },
  { id: "buddha", num: "09", name: "Buddha", devanagari: "बुद्ध",
    theme: "Reflection, compassion and inner balance",
    feature: "Remedies, Temple & Sankalp",
    body: "Devotional practices, personal commitments and practical actions — the quiet grammar of grace.",
    cta: "Explore My Sankalp" },
  { id: "kalki", num: "10", name: "Kalki", devanagari: "कल्कि",
    theme: "The journey toward a wiser future",
    feature: "Future Timeline & Life Report",
    body: "A beautifully designed preview of life themes, time windows and your personalised report.",
    cta: "Explore My Future" },
];

const APP_URL = "/app";
const COMMUNITY_URL = "https://chat.whatsapp.com/Ctbaawn4pCLLLkmvbmTiry?mode=gi_t";
const INNER_MIRROR_URL = "https://www.superlife.living/inner-mirror";

const INTENTIONS = [
  { id: "love", label: "Love & Relationships" },
  { id: "career", label: "Career & Success" },
  { id: "money", label: "Money & Abundance" },
  { id: "health", label: "Health & Wellbeing" },
  { id: "peace", label: "Peace & Happiness" },
  { id: "purpose", label: "Purpose & Dharma" },
];

/* ---------- Reveal wrapper (opacity + lift on enter) ---------- */
function Reveal({ children, delay = 0, className = "", testid }) {
  return (
    <motion.div
      className={className}
      data-testid={testid}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, delay, ease: [0.19, 1, 0.22, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* ---------- Parallax Avatar (Layer 1) ---------- */
function AvatarParallax({ src, alt, side = "right", eager = false }) {
  const wrapRef = useRef(null);
  const prefersReduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start end", "end start"],
  });
  // Avatar moves slower than the content: ~60px up over its lifecycle
  const yAvatar = useTransform(scrollYProgress, [0, 1], prefersReduced ? [0, 0] : [60, -60]);
  const yHalo   = useTransform(scrollYProgress, [0, 1], prefersReduced ? [0, 0] : [30, -30]);

  return (
    <div
      ref={wrapRef}
      className={`dl-avatar-wrap dl-avatar-side-${side}`}
      data-testid={`avatar-${alt?.toLowerCase?.() || "kalki"}`}
    >
      <motion.div className="dl-avatar-halo" style={{ y: yHalo }} aria-hidden="true" />
      <motion.img
        src={src}
        alt={alt}
        className="dl-avatar"
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        style={{ y: yAvatar }}
        draggable={false}
      />
    </div>
  );
}

export default function DivineLanding() {
  const { user: authUser } = useAuth();

  const [selectedIntention, setSelectedIntention] = useState(null);
  const [freeText, setFreeText] = useState("");
  const [wisdomBusy, setWisdomBusy] = useState(false);
  const [wisdom, setWisdom] = useState(null);

  // Auth modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("signup");
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formConfirm, setFormConfirm] = useState("");
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [formInfo, setFormInfo] = useState("");

  // Mount guard — paint dotted-grid background only while this page lives
  useEffect(() => {
    document.body.classList.add("dl-active");
    document.title = "KALKI — The Inner Light | superlife.living";

    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("signin") === "1") {
        setModalMode("signin"); setModalOpen(true);
        window.history.replaceState({}, "", window.location.pathname);
      }
    } catch (_) {}

    return () => document.body.classList.remove("dl-active");
  }, []);

  const requestWisdom = async () => {
    if (!selectedIntention) return;
    setWisdomBusy(true);
    try {
      const { data } = await api.post("/landing/wisdom", {
        intention: selectedIntention,
        message: freeText.trim() || undefined,
      });
      setWisdom(data);
    } catch (e) {
      setWisdom({ suggestions: [{ title: "Pause and Breathe",
        body: "Please try again in a moment — the server is catching its breath." }], source: "error" });
    } finally { setWisdomBusy(false); }
  };

  const openStart = (mode = "signup") => {
    if (authUser) { window.location.href = APP_URL; return; }
    setModalMode(mode); setModalOpen(true);
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submitAuth = async () => {
    setFormError(""); setFormInfo("");
    const email = (formEmail || "").trim();
    const pwd = formPassword || "";
    if (!email) { setFormError("Please enter your email."); return; }
    if (modalMode !== "forgot" && pwd.length < 8) { setFormError("Password must be at least 8 characters."); return; }
    if (modalMode === "signup" && pwd !== formConfirm) { setFormError("Passwords don't match."); return; }
    setFormBusy(true);
    try {
      if (modalMode === "signup") {
        await api.post("/auth/signup", { email, password: pwd, name: formName.trim() || undefined });
        window.location.href = APP_URL;
      } else if (modalMode === "signin") {
        await api.post("/auth/login", { email, password: pwd });
        window.location.href = APP_URL;
      } else {
        await api.post("/auth/forgot-password", { email });
        setFormInfo("If this email is registered, a reset link is on its way.");
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      let msg = (typeof d === "string") ? d
        : (d && typeof d.message === "string") ? d.message
        : Array.isArray(d) ? d.map((e) => e.msg || "").filter(Boolean).join(" ")
        : (err?.message || "Something went wrong. Please try again.");
      setFormError(msg);
    } finally { setFormBusy(false); }
  };

  return (
    <div className="dl-root" data-testid="divine-landing">
      {/* -------------------- NAV -------------------- */}
      <nav className="dl-nav" data-testid="dl-nav">
        <div className="dl-nav-brand">KALKI</div>
        <div className="dl-nav-right">
          <button className="dl-nav-link" data-testid="dl-nav-features" onClick={() => scrollToId("avatars")}>Avatars</button>
          <button className="dl-nav-link" data-testid="dl-nav-method" onClick={() => scrollToId("method")}>Method</button>
          <button className="dl-nav-link" data-testid="dl-nav-chat" onClick={() => scrollToId("chat")}>Chat</button>
          {!authUser && (
            <button className="dl-nav-signin" data-testid="dl-nav-signin"
              onClick={() => openStart("signin")}>Sign In</button>
          )}
          <a className="dl-nav-cta" href={authUser ? APP_URL : "#"}
            data-testid="dl-nav-cta"
            onClick={(e) => { if (!authUser) { e.preventDefault(); openStart("signup"); } }}>
            {authUser ? "Open KALKI" : "Begin"} →
          </a>
        </div>
      </nav>

      {/* ============================================================
          SECTION A · HERO — 10-kalki
          ============================================================ */}
      <section id="hero" className="dl-section dl-hero" data-testid="dl-section-hero">
        <div className="dl-section-inner">
          <Reveal className="dl-content" testid="dl-hero-content">
            <div className="dl-eyebrow">KALKI · THE INNER LIGHT</div>
            <h1 className="dl-h1">
              The wisdom of the ancients,<br/>
              <span className="dl-grad">spoken for your life.</span>
            </h1>
            <p className="dl-lead">
              KALKI is a quiet, contemporary companion that blends Vedic timing and classical reflection
              with careful conversation. No fear. No prophecy. Just honest guidance for the life you're living.
            </p>

            <div className="dl-cta-row">
              <button className="dl-cta" data-testid="dl-cta-hero-begin" onClick={() => openStart("signup")}>
                Begin Your Journey →
              </button>
              <button className="dl-cta secondary" data-testid="dl-cta-hero-talk" onClick={() => scrollToId("chat")}>
                Talk to KALKI
              </button>
            </div>

            <div className="dl-hero-meta">
              <span>✦ Vedic computations</span>
              <span>✦ Private &amp; respectful</span>
              <span>✦ Voice or text</span>
            </div>
          </Reveal>

          <AvatarParallax src={DASH.kalki} alt="Kalki" side="right" eager />
        </div>
      </section>

      {/* ============================================================
          SECTION B · WHAT IS KALKI — 08-krishna
          ============================================================ */}
      <section id="what" className="dl-section dl-reverse" data-testid="dl-section-what">
        <div className="dl-section-inner">
          <Reveal className="dl-content" testid="dl-what-content">
            <div className="dl-eyebrow">What is KALKI</div>
            <h2 className="dl-h2">A gentle intelligence,<br/>rooted in <span className="dl-grad">ten timeless lessons.</span></h2>
            <p className="dl-speak">
              The Dashavatara — ten divine descents — is Vedic India's way of saying: wisdom returns in the shape
              each era needs. KALKI brings those ten lessons into a single, contemporary companion for your life.
            </p>
            <p className="dl-lead">
              Share a question, a hope, a worry. KALKI listens, computes your Vedic timing, and responds with
              clarity you can act on today — not vague fortune-telling.
            </p>
            <div className="dl-cta-row">
              <button className="dl-cta" data-testid="dl-cta-what-explore" onClick={() => scrollToId("avatars")}>
                Explore the Ten Avatars ↓
              </button>
            </div>
          </Reveal>

          <AvatarParallax src={DASH.krishna} alt="Krishna" side="left" />
        </div>
      </section>

      {/* ============================================================
          SECTION C · INTENTION + PRE-LOGIN WISDOM
          ============================================================ */}
      <section id="intention" className="dl-section dl-single" data-testid="dl-section-intention">
        <Reveal className="dl-content dl-center" testid="dl-intention-content">
          <div className="dl-eyebrow">A small taste</div>
          <h2 className="dl-h2">What does your heart <span className="dl-grad">seek today?</span></h2>
          <p className="dl-speak dl-center">
            Pick a theme. KALKI will offer three short reflections — a gentle preview of how it listens.
          </p>

          <div className="dl-intent-grid" data-testid="dl-intent-grid">
            {INTENTIONS.map((it) => (
              <button key={it.id}
                data-testid={`dl-intent-${it.id}`}
                className={`dl-intent ${selectedIntention === it.id ? "selected" : ""}`}
                onClick={() => { setSelectedIntention(it.id); setWisdom(null); }}>{it.label}</button>
            ))}
          </div>

          <input className="dl-free-input" data-testid="dl-intent-freetext"
            placeholder="Or tell me in your own words…"
            value={freeText} maxLength={240}
            onChange={(e) => setFreeText(e.target.value)} />

          <div className="dl-cta-row dl-center-row">
            <button className="dl-cta" data-testid="dl-cta-wisdom"
              disabled={!selectedIntention || wisdomBusy}
              onClick={requestWisdom}>
              {wisdomBusy ? "Listening…" : "Receive a Reflection →"}
            </button>
            {wisdom && (
              <button className="dl-cta secondary" data-testid="dl-cta-another"
                onClick={() => { setWisdom(null); setSelectedIntention(null); setFreeText(""); }}>
                Try Another Intention
              </button>
            )}
          </div>

          {wisdom?.suggestions && (
            <div className="dl-wisdom-stack" data-testid="dl-wisdom-list">
              {wisdom.suggestions.map((w, i) => (
                <Reveal key={i} delay={i * 0.08} className="dl-wisdom" testid={`dl-wisdom-${i}`}>
                  <p className="dl-wisdom-n" aria-hidden="true">0{i + 1}</p>
                  <p className="dl-wisdom-t">{w.title}</p>
                  <p className="dl-wisdom-b">{w.body}</p>
                </Reveal>
              ))}
            </div>
          )}
        </Reveal>
      </section>

      {/* ============================================================
          SECTION D · "TEN AVATARS. TEN DIVINE POWERS."
          ============================================================ */}
      <section id="avatars" className="dl-section dl-single dl-section-header" data-testid="dl-section-avatars-header">
        <Reveal className="dl-content dl-center">
          <div className="dl-eyebrow">The Dashavatara</div>
          <h2 className="dl-h2">Ten avatars.<br/><span className="dl-grad">Ten divine powers inside KALKI.</span></h2>
          <p className="dl-speak dl-center">
            Each divine descent represents a timeless capability. Together, they shape the ten things
            KALKI can quietly do for your life.
          </p>
        </Reveal>
      </section>

      {/* ============================================================
          SECTION E · TEN ALTERNATING FEATURE SECTIONS
          ============================================================ */}
      {DASHAVATARA.map((d, i) => {
        const reverse = i % 2 === 1; // alternate left/right
        return (
          <section
            key={d.id}
            id={`avatar-${d.id}`}
            className={`dl-section dl-feature ${reverse ? "dl-reverse" : ""}`}
            data-testid={`dl-feature-${d.id}`}
          >
            <div className="dl-section-inner">
              <Reveal className="dl-content" testid={`dl-feature-content-${d.id}`}>
                <div className="dl-feature-num">{d.num} · <span>{d.devanagari}</span></div>
                <h3 className="dl-feature-name">{d.name}</h3>
                <div className="dl-feature-theme">{d.theme}</div>

                <div className="dl-feature-card">
                  <div className="dl-eyebrow dl-eyebrow-sm">KALKI Capability</div>
                  <p className="dl-feature-ft">{d.feature}</p>
                  <p className="dl-feature-fb">{d.body}</p>
                </div>

                <div className="dl-cta-row">
                  <button className="dl-cta" data-testid={`dl-feature-cta-${d.id}`}
                    onClick={() => openStart("signup")}>{d.cta} →</button>
                </div>
              </Reveal>

              <AvatarParallax src={DASH[d.id]} alt={d.name} side={reverse ? "left" : "right"} />
            </div>
          </section>
        );
      })}

      {/* ============================================================
          SECTION F · CHAT / TALK SHOWCASE
          ============================================================ */}
      <section id="chat" className="dl-section dl-single" data-testid="dl-section-chat">
        <Reveal className="dl-content dl-wide dl-center">
          <div className="dl-eyebrow">Conversation</div>
          <h2 className="dl-h2">You have questions.<br/><span className="dl-grad">KALKI is here to listen.</span></h2>
          <p className="dl-speak dl-center">
            Bring your questions, hopes, uncertainties and dreams. Voice or text — KALKI understands,
            computes, and responds with care.
          </p>

          <div className="dl-chat-preview" data-testid="dl-chat-preview">
            <div className="dl-bubble user">How can I make better choices in life?</div>
            <div className="dl-bubble kalki">Begin by understanding what truly matters to you. When your actions reflect your values, each step gains meaning.</div>
            <div className="dl-bubble user">Can you help me understand my future?</div>
            <div className="dl-bubble kalki">With your birth details, we can explore your Vedic chart, traditional timing patterns, and the choices before you — gently, together.</div>
          </div>

          <div className="dl-cta-row dl-center-row">
            <button className="dl-cta" data-testid="dl-cta-chat-talk" onClick={() => openStart("signup")}>
              Talk to KALKI →
            </button>
            <button className="dl-cta secondary" data-testid="dl-cta-chat-report" onClick={() => openStart("signup")}>
              Get My Life Report
            </button>
          </div>
        </Reveal>
      </section>

      {/* ============================================================
          SECTION G · METHODOLOGY — VEDIC × AI
          ============================================================ */}
      <section id="method" className="dl-section dl-single" data-testid="dl-section-method">
        <Reveal className="dl-content dl-wide dl-center">
          <div className="dl-eyebrow">Methodology</div>
          <h2 className="dl-h2">Vedic computations.<br/><span className="dl-grad">Human-shaped language.</span></h2>
          <p className="dl-speak dl-center">
            Every insight begins with a classical computation — Lagna, planets, nakshatras, dashas, Shadbala,
            Ashtakavarga — and ends in a sentence a human can actually use today.
          </p>

          <div className="dl-method-grid">
            <div className="dl-method-card" data-testid="dl-method-step-1">
              <div className="dl-method-num">01</div>
              <h4>Compute</h4>
              <p>Birth details become a precise Vedic chart with timing windows and classical balances.</p>
            </div>
            <div className="dl-method-card" data-testid="dl-method-step-2">
              <div className="dl-method-num">02</div>
              <h4>Reflect</h4>
              <p>KALKI reads the chart through tradition — not prediction, but context and meaning.</p>
            </div>
            <div className="dl-method-card" data-testid="dl-method-step-3">
              <div className="dl-method-num">03</div>
              <h4>Respond</h4>
              <p>Guidance arrives as clear language and small, humane actions you can take today.</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ============================================================
          SECTION H · FINAL DARSHAN + CTA
          ============================================================ */}
      <section id="darshan" className="dl-section dl-darshan" data-testid="dl-section-darshan">
        <div className="dl-darshan-mandala" aria-hidden="true" />
        <Reveal className="dl-darshan-inner">
          <AvatarParallax src={DASH.kalki} alt="Kalki Darshan" side="center" />
          <h2 className="dl-h1 dl-darshan-head">
            The light you seek is <span className="dl-grad">already within you.</span>
          </h2>
          <p className="dl-speak dl-center" style={{ maxWidth: 620, margin: "20px auto" }}>
            You came seeking answers. May you leave with a little more clarity, a little more courage,
            and a little more peace. Your journey begins with one gentle step.
          </p>

          <div className="dl-cta-row dl-center-row">
            <button className="dl-cta" data-testid="dl-cta-final-begin" onClick={() => openStart("signup")}>
              Begin My Journey With KALKI →
            </button>
            <button className="dl-cta secondary" data-testid="dl-cta-final-talk" onClick={() => openStart("signup")}>
              Talk to KALKI
            </button>
          </div>

          <div className="dl-darshan-brand">KALKI — THE INNER LIGHT</div>
          <div className="dl-darshan-close">
            Let love be our Dharma. Let kindness be our Karma.
          </div>

          <div className="dl-footer-links">
            <a href={COMMUNITY_URL} target="_blank" rel="noopener noreferrer" data-testid="dl-footer-community">Join the KALKI Family</a>
            <span>·</span>
            <a href={INNER_MIRROR_URL} data-testid="dl-footer-inner-mirror">Inner Mirror</a>
            <span>·</span>
            <a href="https://www.superlife.living" target="_blank" rel="noopener noreferrer" data-testid="dl-footer-site">superlife.living</a>
          </div>
        </Reveal>
      </section>

      {/* -------------------- AUTH MODAL -------------------- */}
      <div className={`dl-modal ${modalOpen ? "open" : ""}`} onClick={() => setModalOpen(false)}>
        <div className="dl-modal-card" onClick={(e) => e.stopPropagation()} data-testid="dl-auth-modal">
          <button className="dl-modal-close" onClick={() => setModalOpen(false)}>CLOSE</button>
          <div className="dl-modal-title">KALKI</div>
          <p className="dl-modal-sub">
            {modalMode === "signin" ? "Welcome back." :
             modalMode === "forgot" ? "We'll help you back in." : "Begin your journey."}
          </p>

          {modalMode !== "forgot" && (
            <>
              <a className="dl-g-btn" href="#" data-testid="dl-modal-google"
                onClick={(e) => {
                  e.preventDefault();
                  const redirectUrl = window.location.origin + APP_URL;
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}>
                <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M23 12.25c0-.8-.07-1.56-.21-2.3H12v4.37h6.17c-.27 1.42-1.06 2.62-2.26 3.42v2.84h3.65c2.13-1.96 3.44-4.85 3.44-8.33z"/><path fill="#34A853" d="M12 23c3.06 0 5.63-1 7.5-2.72l-3.65-2.84c-1.01.68-2.3 1.08-3.85 1.08-2.96 0-5.47-2-6.37-4.7H1.86v2.95C3.72 20.4 7.56 23 12 23z"/><path fill="#FBBC05" d="M5.63 13.82c-.23-.68-.37-1.4-.37-2.14 0-.74.13-1.46.37-2.14V6.59H1.86C1.3 7.98 1 9.46 1 11c0 1.54.3 3.02.86 4.41l3.77-2.95z"/><path fill="#EA4335" d="M12 4.75c1.67 0 3.17.58 4.35 1.72l3.26-3.26C17.63 1.4 15.06.5 12 .5 7.56.5 3.72 3.1 1.86 6.59l3.77 2.95c.9-2.7 3.41-4.79 6.37-4.79z"/></svg>
                Continue with Google
              </a>
              <div className="dl-divider">OR</div>
            </>
          )}

          {modalMode === "signup" && (
            <input placeholder="First name" value={formName} onChange={(e)=>setFormName(e.target.value)} data-testid="dl-auth-name" />
          )}
          <input placeholder="Email" type="email" value={formEmail} onChange={(e)=>setFormEmail(e.target.value)} data-testid="dl-auth-email" />
          {modalMode !== "forgot" && (
            <input placeholder="Password" type="password" value={formPassword} onChange={(e)=>setFormPassword(e.target.value)} data-testid="dl-auth-password" />
          )}
          {modalMode === "signup" && (
            <input placeholder="Confirm password" type="password" value={formConfirm} onChange={(e)=>setFormConfirm(e.target.value)} data-testid="dl-auth-confirm" />
          )}

          {formError && <div data-testid="dl-auth-error" style={{ color: "#dc2626", fontSize: 12, marginTop: 8, textAlign: "center" }}>{formError}</div>}
          {formInfo && <div data-testid="dl-auth-info" style={{ color: "#059669", fontSize: 12, marginTop: 8, textAlign: "center" }}>{formInfo}</div>}

          <button className="dl-modal-submit" disabled={formBusy} onClick={submitAuth}
            data-testid={modalMode === "signin" ? "dl-modal-signin-submit"
                       : modalMode === "forgot" ? "dl-modal-forgot-submit" : "dl-modal-signup-submit"}>
            {formBusy ? "Please wait…" :
             modalMode === "signin" ? "Sign in" :
             modalMode === "forgot" ? "Send reset link" : "Create account"}
          </button>

          <div className="dl-modal-switch">
            {modalMode === "signup" ? (
              <>Already have an account? <a onClick={() => { setModalMode("signin"); setFormError(""); setFormInfo(""); }} data-testid="dl-modal-switch-signin">Sign in</a></>
            ) : modalMode === "signin" ? (
              <>New here? <a onClick={() => { setModalMode("signup"); setFormError(""); setFormInfo(""); }} data-testid="dl-modal-switch-signup">Create account</a>
                {" · "}
                <a onClick={() => { setModalMode("forgot"); setFormError(""); setFormInfo(""); }} data-testid="dl-modal-switch-forgot">Forgot password?</a>
              </>
            ) : (
              <a onClick={() => { setModalMode("signin"); setFormError(""); setFormInfo(""); }} data-testid="dl-modal-switch-back">Back to sign in</a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
